import last from 'lodash/last';

import type { HttpClient } from './http-client.main';
import type { Body } from './http-client.types';

export class RpcException extends Error {
  public readonly error: number;

  constructor(message: string, public code: number, public details: any) {
    super(message);

    this.error = code;
  }
}

export type RpcCallback = (body: RpcFetchBody) => void;

export type RpcResponseSuccess<R> = {
  name: string;
  result: R;
  extra?: any;
};

export type RpcResponseError = {
  name: string;
  error: number;
  message: string;
  details: any;
};

export type RpcResponse<R> = RpcResponseSuccess<R> | RpcResponseError;

function isResponseError<R>(response: RpcResponse<R>): response is RpcResponseError {
  return 'error' in response;
}

export type RpcFetchBody = {
  auth?: {
    logged: boolean
  },
  responses: Array<RpcResponseSuccess<any> | RpcResponseError>
};

export type CallRemoteProcedureResult<R> = {
  auth: RpcFetchBody['auth'];
  response: RpcResponseSuccess<R>;
};

function logMessage(name: string, message?: string): string {
  return `call method '${name}'${message ? `: ${message}` : ''}`;
}

export class RpcClient {
  private counter = 0;

  private listeners: RpcCallback[] = [];

  constructor(
    private httpClient: HttpClient,
    private options: {
      withAuthorization: boolean;
      log: (m: { num: number; type: string; message: string; data: any }) => void;
      onException: <T extends { message: string, error: Error }>(t: T) => void;
      enviromentOrigin?: string;
    },
  ) {}

  public subscribe(callback: RpcCallback): void {
    this.listeners.push(callback);
  }

  public callRemoteProcedure(name: 'accounts.getVerifiedEmails', params: {}, logInfo?: string): Promise<CallRemoteProcedureResult<string[]>>;
  public callRemoteProcedure(name: 'base.modifiers', params: {}, logInfo?: string): Promise<CallRemoteProcedureResult<{
    '1': string[];
    '2': string[];
  }>>;
  public callRemoteProcedure(name: 'applications.getConfigureProperties', params: { applicationCode: string; platform: string }, logInfo?: string): Promise<CallRemoteProcedureResult<{
    email_from_email?: string;
    email_from_name?: string;
    email_reply_email?: string;
    email_reply_name?: string;
  }>>;
  public callRemoteProcedure(name: 'accounts.getLanguages', params: {}, logInfo?: string): Promise<CallRemoteProcedureResult<Array<{
    code: string;
    name: string;
    subscribers: number;
  }>>>;
  public callRemoteProcedure(
    name: string,
    params: any,
    logInfo?: string,
  ): Promise<CallRemoteProcedureResult<any>> {
    return this.callRemoteProcedureBase('_rpc', { calls: [{ name, params }] }, logMessage(name, logInfo));
  }

  public callRemoteProcedureWithFiles(
    name: string,
    params: any,
    files: any,
    logInfo?: string,
  ): Promise<CallRemoteProcedureResult<any>> {
    const body = new FormData();
    body.append('name', name);
    body.append('params', JSON.stringify(params));
    Object.keys(files).forEach((key) => {
      const fileName = last(key.split('.')) as string;
      body.append(fileName, files[key]);
    });
    return this.callRemoteProcedureBase(`_upload_${name}`, body, logMessage(`${name} (upload files)`, logInfo));
  }

  public callRemoteProcedureBase(
    url: string,
    body: any,
    logInfo: string,
  ): Promise<CallRemoteProcedureResult<any>> {
    return this.fetch(url, body, logInfo).then(({ auth, responses }) => {
      const response = responses[0];
      if (!response) {
        throw new RpcException('no result', 500, {});
      }

      if (isResponseError(response)) {
        throw new RpcException(response.message, response.error, response.details);
      }

      return { auth, response };
    });
  }

  public callMany(
    calls: Array<{ name: string; params?: any }>,
    { logInfo } : { logInfo: string },
  ): Promise<RpcFetchBody> {
    const rpcNamesArr = calls.map((call) => call.name);
    const logStr = `${rpcNamesArr.join('|')}|||${logInfo}`;
    return this.fetch('_rpc', { calls }, logStr);
  }

  private fetch(url: string, body: Body, logStr: string): Promise<RpcFetchBody> {
    const { enviromentOrigin = '' } = this.options;
    // eslint-disable-next-line no-plusplus
    const num = ++this.counter;
    this.options.log({
      num,
      type: 'callStart',
      message: logStr,
      data: body,
    });
    return this.httpClient.post<RpcFetchBody, never, never, any>(
      `${enviromentOrigin}/cp/${url}?l=${encodeURI(logStr)}`,
      {
        body,
        withAuthorization: this.options.withAuthorization,
      },
    ).then((resultBody) => {
      this.options.log({
        num,
        type: 'callEnd',
        message: logStr,
        data: resultBody,
      });
      this.listeners.forEach((listener) => {
        try {
          listener(resultBody);
        } catch (error: any) {
          this.options.onException({
            message: 'RPC listener error',
            error,
          });
        }
      });
      return resultBody;
    });
  }
}
